import moment, { Moment, MomentInput, MomentFormatSpecification } from 'moment-timezone';

const FALLBACK_TIMEZONE = 'Asia/Taipei';
// 檢查字串是否以「年-月-日 時:分」開頭，日期和時間之間也可以用 T。
const DATE_TIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;
// 檢查字串結尾是否有時區，例如 Z（UTC）、+08:00 或 +0800。
const EXPLICIT_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL_FORMATS = [
	'YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm',
	'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD', 'YYYY-MM', 'YYYY',
];

export const resolveTimeZone = (timezone?: string | null): string => (
	timezone && moment.tz.zone(timezone) ? timezone : FALLBACK_TIMEZONE
);

// 有 offset 的值是時間點；無 offset 的表單值直接在目標時區解析，不能先經裝置時區。
export const parseZonedDateTime = (
	value?: MomentInput,
	timezone?: string,
	format?: MomentFormatSpecification,
): Moment => {
	const zone = resolveTimeZone(timezone);
	if (value === undefined) return moment.utc().tz(zone);
	if (typeof value === 'string') {
		if (EXPLICIT_OFFSET.test(value)) return moment.parseZone(value, moment.ISO_8601, true).tz(zone);
		return moment.tz(value, format || LOCAL_FORMATS, true, zone);
	}
	return moment.tz(value, zone);
};

// 頁面內可用同一 parser 處理 now、API 時間點、當地表單字串與 Moment。
export const createZonedMoment = (timezone?: string) => (
	(value?: MomentInput, format?: MomentFormatSpecification): Moment => parseZonedDateTime(value, timezone, format)
);

export const formatUserDateTime = (
	value: string | null | undefined,
	timezone?: string,
	format = 'YYYY-MM-DD HH:mm',
): string | null | undefined => {
	if (!value || !DATE_TIME.test(value)) return value;
	return parseZonedDateTime(value, timezone).format(format);
};

// API 紀錄的舊格式原樣顯示；只有帶明確時區的完整 datetime 才轉員工時區。
export const formatUserRecordTime = (
	value: string | null | undefined,
	timezone?: string,
): string | null | undefined => {
	if (!value || !DATE_TIME.test(value) || !EXPLICIT_OFFSET.test(value)) return value;
	const date = parseZonedDateTime(value, timezone);
	return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : '--';
};

// 純日期／時分／月份／年份不轉換。未指定時區時以 Asia/Taipei 解析完整日期時間。
export const toUtcDateTime = (value: string | null | undefined, timezone?: string): string | null | undefined => {
	if (!value || !DATE_TIME.test(value)) return value;
	return parseZonedDateTime(value, timezone).toISOString();
};

export const combinePickerDateTime = (date: Moment, time: Moment, timezone?: string): Moment => (
	parseZonedDateTime(`${date.format('YYYY-MM-DD')} ${time.format('HH:mm')}`, timezone)
);
